import { getComponentRegistryConfig, getQueryParameter, storage } from '@b2x/shared';

import type { BusinessContext } from '../types/available-type';
import type { PersistedState } from '../types/profile-type';

import { BusinessType, ProfileSource, QUERY_PARAMS } from '../types/enums';
import { PROFILE_PERSISTED_CONNECTION } from '../utilities/constants';

/**
 * Order of getters are ordered by priority (starting from top)
 */
const businessTypeCondition = {
  get emhContextConfig(): Promise<BusinessContext<BusinessType | null>> {
    return (async () => {
      const [emhContextBarConfig] = await getComponentRegistryConfig((components) => {
        return [components.find((component) => component.payload?.userContextType)] as any[];
      });

      return {
        source: ProfileSource.PAGE,
        businessType: emhContextBarConfig?.payload?.forceUserContextType
          ? (emhContextBarConfig.payload?.userContextType.toUpperCase() as BusinessType) || null
          : null,
        profileId: null,
      };
    })();
  },
  get urlPathAvailable(): Promise<BusinessContext<BusinessType | null>> {
    return (async () => {
      let businessType = null;
      let profileId = null;
      const isB2cPage = location.href.includes('/my-area');
      const isB2bPage = location.href.includes('/my-area/business');
      const hasLandedDirectlyOnVansPage = document.referrer === '' && location.href.includes('/vans');

      // I know this is crazy but while we don't have page properties we probably have to keep increasing this list :(
      const contextAgnosticPages = ['/profile-overview', '/my-wishlist', '/logout-successful'];
      if (contextAgnosticPages.some((url) => location.href.includes(url))) {
        businessType = null;
      } else if (isB2bPage || hasLandedDirectlyOnVansPage) {
        // @ts-ignore
        businessType = BusinessType.B2B;
        profileId =
          storage.session.get<Record<string, any>>('swsp:B2X_ACTIVE_PROFILE')?.company?.domainRef?.meId || null;
      } else if (isB2cPage) {
        // @ts-ignore
        businessType = BusinessType.B2C;
      }
      return {
        source: ProfileSource.PAGE,
        businessType,
        profileId,
      };
    })();
  },
  get ctxAvailable(): Promise<BusinessContext<BusinessType | null>> {
    return (async () => {
      let ctx = getQueryParameter(QUERY_PARAMS.CTX) as BusinessType | null;

      if (ctx) {
        ctx = ctx.toUpperCase() as BusinessType;
      }

      return {
        source: ProfileSource.URL,
        businessType: ctx,
        profileId: null,
      };
    })();
  },
  get localAvailable(): Promise<BusinessContext<BusinessType | undefined | null>> {
    return (async () => {
      const { businessType, profileId = null } = storage.local.get<PersistedState>(PROFILE_PERSISTED_CONNECTION) || {};

      return {
        source: ProfileSource.PERSISTED,
        businessType,
        profileId, // remember last used company profile
      };
    })();
  },
};

const validBusinessTypes = [BusinessType.B2B, BusinessType.B2C];

export async function resolveBusinessContext(): Promise<BusinessContext<BusinessType>> {
  const { profileId = null } = storage.local.get<PersistedState>(PROFILE_PERSISTED_CONNECTION) || {};

  const businessContextResult: BusinessContext<BusinessType> = {
    source: ProfileSource.PERSISTED,
    businessType: BusinessType.B2C,
    profileId,
  };

  for await (const businessContext of Object.values(businessTypeCondition)) {
    if (businessContext.businessType && validBusinessTypes.includes(businessContext.businessType)) {
      const { source, businessType, profileId } = businessContext as BusinessContext<BusinessType>;

      businessContextResult.businessType = businessType;
      businessContextResult.source = source;

      if (profileId) {
        businessContextResult.profileId = profileId;
      }

      return businessContextResult;
    }
  }

  return businessContextResult;
}
